import React, {useState, useEffect, useCallback} from "react";
import {Table, Modal, Typography, Descriptions, Button, Space, Col, Select, Row} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import theme from "../theme";
import sectionalsService from "../services/SectionalsService";
import {COT_ROLE, DISTRICT_ROLE, SECTIONAL_ROLE, useUserRole} from "../utils/userSessionUtils";
import {useNavigate} from "react-router-dom";
import {CreditCardOutlined, EyeOutlined, SearchOutlined, UndoOutlined} from "@ant-design/icons";
import {useSelector} from "react-redux";

const {Title} = Typography;

const SectionalsPage = () => {
    const [sectionals, setSectionals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSectional, setSelectedSectional] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const userRole = useUserRole();
    const isMobile = useMediaQuery({maxWidth: 800});
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth || {});
    const user = auth.user || {};
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const { entityId, districtId, route: userSections  } = user;

    const [tempFilters, setTempFilters] = useState({
        sectionId: [],
    });

    const [filters, setFilters] = useState({
        sectionId: { value: userSections, operator: "in" },
        districtId: { value: districtId, operator: "eq" },
        entityId: { value: entityId, operator: "eq" },
    });

    const fetchSectionals = useCallback(async (appliedFilters, page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const response = await sectionalsService.getAllWithPagination({
                ...appliedFilters
            }, page, pageSize);
            setSectionals(response.data);
        } catch (error) {
            console.error("Error fetching sectionals:", error);
        } finally {
            setLoading(false);
        }
    }, [setSectionals, setLoading]);

    useEffect(() => {
        const initialFilters = {
            sectionId: {value: userSections, operator: "in"},
            districtId: {value: districtId, operator: "eq"},
            entityId: {value: entityId, operator: "eq"},
        };
        fetchSectionals(initialFilters).catch(console.error);
    }, [districtId, entityId, fetchSectionals, userSections]);

    const handleRowClick = (record) => {
        setSelectedSectional(record);
        setIsModalVisible(true);
    };

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const applyFilters = () => {
        const newFilters = {
            sectionId: {value: tempFilters.sectionId, operator: "in"},
            districtId: {value: districtId, operator: "eq"},
            entityId: {value: entityId, operator: "eq"},
        };
        setFilters(newFilters);
        fetchSectionals(newFilters).catch(console.error);
    };

    const resetFilters = () => {
        const resetTempFilters = {
            sectionId: [],
        };
        const resetAppliedFilters = {
            sectionId: {value: userSections, operator: "in"},
            districtId: {value: districtId, operator: "eq"},
            entityId: {value: entityId, operator: "eq"},
        };
        setTempFilters(resetTempFilters);
        setFilters(resetAppliedFilters);
        fetchSectionals(resetAppliedFilters).catch(console.error);
    };

    const desktopColumns = [
        {title: "Nombre Completo", dataIndex: "fullName", key: "fullName"},
        {title: "Clave de Elector", dataIndex: "electorKey", key: "electorKey"},
        {title: "Sección", key: "section", render: (record) => record.sections ? record.sections.join(", ") : ""},
        {title: "Total actual", dataIndex: "count", key: "count"},
        {
            title: "Acciones",
            key: "actions",
            render: (record) => (
                <Space size="middle">
                    <Button
                        type="default"
                        icon={<EyeOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/people-as-user/${record.electorKey}/SECTIONALS`);
                        }}
                    >
                        Ver Registros
                    </Button>
                    {false && (record.roles.includes(SECTIONAL_ROLE) && [COT_ROLE, DISTRICT_ROLE].includes(userRole)) && (
                        <Button
                            type="default"
                            icon={<CreditCardOutlined/>}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/informacion-nomina/${record.electorKey}`);
                            }}
                        >
                            Ver información de nómina
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const mobileColumns = [
        {
            title: "Nombre",
            dataIndex: "fullName",
            key: "fullName",
            render: (text) => text.split(" ")[0] + " " + text.split(" ")[1] + " " + text.split(" ")[2],
        },
        {title: "Registros", dataIndex: "count", key: "count"},
        {
            title: "",
            key: "actions",
            render: (record) => (
                <Space size="middle">
                    <Button
                        type="default"
                        icon={<EyeOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/people-as-user/${record.electorKey}/SECTIONALS`);
                        }}
                    />
                    {false && (record.roles.includes(SECTIONAL_ROLE) && [COT_ROLE, DISTRICT_ROLE].includes(userRole)) && (
                        <Button
                            type="default"
                            icon={<CreditCardOutlined/>}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/informacion-nomina/${record.electorKey}`);
                            }}
                        />
                    )}
                </Space>
            ),
        },
    ];

    const handleTableChange = (paginationInfo) => {
        setPagination({
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        });
        fetchSectionals(filters, paginationInfo.current, paginationInfo.pageSize).catch(console.error);
    };

    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Title level={2} style={{textAlign: "center"}}>
                    Lista de seccionales
                </Title>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    <Col xs={24} sm={24} md={24}>
                        <Select
                            mode="multiple"
                            placeholder="Filtrar por Sección"
                            value={tempFilters.sectionId}
                            onChange={(value) => handleFilterChange("sectionId", value)}
                            style={{width: "100%"}}
                            options={Array.from(new Set(userSections)).map(sec => ({
                                label: `Sección ${sec}`,
                                value: sec
                            }))}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={24}>
                        <Space style={{float: "right"}}>
                            <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                                Aplicar Filtros
                            </Button>
                            <Button icon={<UndoOutlined/>} onClick={resetFilters}>Restablecer</Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={isMobile ? mobileColumns : desktopColumns}
                    dataSource={sectionals}
                    rowKey="electorKey"
                    loading={loading}
                    scroll={{x: "max-content"}}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: {cursor: "pointer"},
                    })}
                />
            </motion.div>

            <Modal
                title="Detalles del seccional"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                {selectedSectional && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Nombre Completo">{selectedSectional.fullName}</Descriptions.Item>
                        <Descriptions.Item label="CURP">{selectedSectional.curp}</Descriptions.Item>
                        <Descriptions.Item label="Clave de Elector">{selectedSectional.electorKey}</Descriptions.Item>
                        <Descriptions.Item label="Entidad">{selectedSectional.state}</Descriptions.Item>
                        <Descriptions.Item label="Sección">{selectedSectional.sections.join(", ")}</Descriptions.Item>
                        <Descriptions.Item label="Roles">{selectedSectional.roles.join(", ")}</Descriptions.Item>
                        <Descriptions.Item label="Total Actual">{selectedSectional.count}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </>
    );
};

export default SectionalsPage;